import { getNationalHoliday } from "../../libs/cognito-api/national-holiday/national-holiday";
import { normalizeDate } from "../../shared/calendar";

const holidaysApi = getNationalHoliday();

export async function getHolidaysTable(dataInicial: string | number, dataFinal?: string | number): Promise<any[][]> {
  const initialDate = normalizeDate(dataInicial);
  const finalDate = emptyDateToUndefined(dataFinal) ?? "9999-12-31";
  const params: { InitialDate: string; FinalDate?: string } = {
    InitialDate: initialDate,
    FinalDate: finalDate,
  };

  const response = await holidaysApi.getHolidaysHolidaysInRange(params);
  const holidays = unwrapData<string[]>(response, "Calendario de feriados nao retornou dados.");

  const rows = holidays
    .map((holidayDate) => {
      const date = normalizeDate(holidayDate);
      return [date, formatWeekday(date)];
    })
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  return [
    ["Data", "Dia da semana"],
    ...rows,
  ];
}

export async function getWorkingDaysTable(dataInicial: string | number, dataFinal: string | number): Promise<any[][]> {
  const initialDate = normalizeDate(dataInicial);
  const finalDate = normalizeDate(dataFinal);

  const response = await holidaysApi.getHolidaysWorkingDaysList({
    InitialDate: initialDate,
    FinalDate: finalDate,
  });
  const workingDays = unwrapData<string[]>(response, "Calendario de dias uteis nao retornou dados.");

  const rows = workingDays
    .map((workingDate) => {
      const date = normalizeDate(workingDate);
      return [date, formatWeekday(date)];
    })
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  return [
    ["Data", "Dia da semana"],
    ...rows,
  ];
}

export async function getWorkingDaysCount(dataInicial: string | number, dataFinal: string | number): Promise<number> {
  const response = await holidaysApi.postHolidaysCountWorkingDays({
    initialDate: normalizeDate(dataInicial),
    finalDate: normalizeDate(dataFinal),
  });

  const count = unwrapData<number>(response, "Quantidade de dias uteis nao retornada.");
  if (typeof count !== "number" || !Number.isFinite(count)) {
    throw new Error("Quantidade de dias uteis nao retornada.");
  }
  return count;
}

export async function addWorkingDays(dataInicial: string | number, quantidadeDiasUteis: number): Promise<string> {
  const initialDate = normalizeDate(dataInicial);
  const offset = normalizeWorkingDayOffset(quantidadeDiasUteis);

  if (offset === 0) return formatBrazilianDate(initialDate);

  const response = offset > 0
    ? await holidaysApi.getHolidaysDateAfterWorkingDays({
      dateStart: initialDate,
      workingDays: offset,
    })
    : await holidaysApi.getHolidaysDateBeforeWorkingDays({
      dateStart: initialDate,
      workingDays: Math.abs(offset),
    });

  const finalDate = normalizeDate(unwrapData<string>(response, "Data final de dias uteis nao retornada."));
  return formatBrazilianDate(finalDate);
}

export async function getWorkingDaysFromTable(dataInicial: string | number, quantidadeDiasUteis: number): Promise<any[][]> {
  const initialDate = normalizeDate(dataInicial);
  const workingDays = normalizeWorkingDays(quantidadeDiasUteis);
  const endDateResponse = await holidaysApi.getHolidaysDateAfterWorkingDays({
    dateStart: initialDate,
    workingDays,
  });
  const finalDate = normalizeDate(unwrapData<string>(endDateResponse, "Data final de dias uteis nao retornada."));
  const table = await getWorkingDaysTable(initialDate, finalDate);

  return [
    table[0],
    ...table.slice(1, workingDays + 1),
  ];
}

function unwrapData<T>(response: any, emptyMessage: string): T {
  if (response?.status === false) {
    throw new Error(response.message || emptyMessage);
  }
  const data = response?.data ?? response;
  if (data == null) {
    throw new Error(response?.message || emptyMessage);
  }
  return data as T;
}

function emptyDateToUndefined(value?: string | number): string | undefined {
  if (value == null || String(value).trim() === "") return undefined;
  return normalizeDate(value);
}

function normalizeWorkingDays(value: number): number {
  const workingDays = Number(value);
  if (!Number.isInteger(workingDays) || workingDays < 1) {
    throw new Error("Quantidade de dias uteis invalida. Use um numero inteiro maior que zero.");
  }
  return workingDays;
}

function normalizeWorkingDayOffset(value: number): number {
  const offset = Number(value);
  if (!Number.isInteger(offset)) {
    throw new Error("Quantidade de dias uteis invalida. Use um numero inteiro.");
  }
  return offset;
}

function formatBrazilianDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

function formatWeekday(isoDate: string): string {
  const labels = ["Domingo", "Segunda-feira", "Terca-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sabado"];
  return labels[new Date(`${isoDate}T00:00:00Z`).getUTCDay()] ?? "";
}
